import React from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { IButtonProps } from '@rbi-ctg/frontend';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { ServiceMode } from 'state/order';
import { useStoreContext } from 'state/store';
import { theme } from 'styles/configure-theme';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';

import {
  IconWrapper,
  ModalContent,
  ModalHeading,
  Mode,
  ModeColumn,
  Section,
  StyledButton,
  TextRow,
  Title,
} from './styled';
import { OnServiceModeSelected } from './types';

interface IModalContentProps {
  selectServiceMode: OnServiceModeSelected;
}

const WrapperButton: React.FC<React.PropsWithChildren<IButtonProps>> = ({
  children,
  disabled,
  onClick,
  testID: dataQaId,
}) => (
  <StyledButton testID={dataQaId} onPress={onClick} disabled={!!disabled}>
    {children}
  </StyledButton>
);

export const PickupMode: React.FC<React.PropsWithChildren<{
  serviceMode: ServiceMode;
  modeAvailable: boolean;
  availableText: string;
  unavailableText: string;
  onSelected: OnServiceModeSelected;
  testID: string;
}>> = ({ serviceMode, modeAvailable, availableText, unavailableText, onSelected, testID }) => (
  <WrapperButton disabled={!modeAvailable} testID={testID} onClick={() => onSelected(serviceMode)}>
    <TextRow>
      <Mode disabled={!modeAvailable}>{modeAvailable ? availableText : unavailableText}</Mode>
    </TextRow>
  </WrapperButton>
);

const PickupModeContent: React.FC<React.PropsWithChildren<IModalContentProps>> = props => {
  const { serviceModeStatus } = useStoreContext();
  const { formatMessage } = useIntl();
  const isOrderBayEnabled = useFlag(LaunchDarklyFlag.ENABLE_ORDER_BAY);

  const driveThruAvailable = serviceModeStatus.DRIVE_THRU.available;
  const takeOutAvailable = serviceModeStatus.TAKEOUT.available;
  const dineInAvailable = serviceModeStatus.EAT_IN.available;
  const curbsideAvailable = serviceModeStatus.CURBSIDE.available;

  const driveThruDisabled = serviceModeStatus.DRIVE_THRU.disabled;
  const takeOutDisabled = serviceModeStatus.TAKEOUT.disabled;
  const dineInDisabled = serviceModeStatus.EAT_IN.disabled;
  const disableCurbside = serviceModeStatus.CURBSIDE.disabled;

  const hasPickupOptionsAvailable = [driveThruAvailable, takeOutAvailable, curbsideAvailable].some(
    Boolean
  );
  const hasDineInOptionsAvailable = [dineInAvailable].some(Boolean);

  return (
    <ModalContent>
      <ModalHeading testID="modal-heading">{formatMessage({ id: 'willPickUpOrder' })}</ModalHeading>
      {hasPickupOptionsAvailable && (
        <Section>
          <IconWrapper>
            <Icon
              variant="cart"
              color={theme.token('icon-active')}
              width="21px"
              height="21px"
              {...hiddenAccessibilityPlatformProps}
            />
          </IconWrapper>
          <ModeColumn>
            <TextRow isTitle>
              <Title>{formatMessage({ id: 'pickUp' })}</Title>
            </TextRow>
            {driveThruAvailable && (
              <PickupMode
                serviceMode={ServiceMode.DRIVE_THRU}
                modeAvailable={!driveThruDisabled}
                availableText={formatMessage({ id: 'driveThru' })}
                unavailableText={formatMessage({ id: 'driveThruUnavailable' })}
                onSelected={props.selectServiceMode}
                testID="pickupmode-drive-thru"
              />
            )}
            {takeOutAvailable && (
              <PickupMode
                serviceMode={ServiceMode.TAKEOUT}
                modeAvailable={!takeOutDisabled}
                availableText={formatMessage({ id: 'takeOut' })}
                unavailableText={formatMessage({ id: 'takeOutUnavailable' })}
                onSelected={props.selectServiceMode}
                testID="pickupmode-take-out"
              />
            )}
            {curbsideAvailable && (
              <PickupMode
                serviceMode={ServiceMode.CURBSIDE}
                modeAvailable={!disableCurbside}
                // TODO: BKPE-6812 ORDER_BAY_PILOT. Remove or consolidate this logic once the pilot is done
                availableText={formatMessage({ id: isOrderBayEnabled ? 'orderBay' : 'curbside' })}
                unavailableText={formatMessage({ id: 'curbsideUnavailable' })}
                onSelected={props.selectServiceMode}
                testID="pickupmode-curbside"
              />
            )}
          </ModeColumn>
        </Section>
      )}
      {hasDineInOptionsAvailable && (
        <Section>
          <IconWrapper>
            <Icon
              variant="restaurant"
              color={theme.token('icon-active')}
              width="21px"
              height="21px"
              {...hiddenAccessibilityPlatformProps}
            />
          </IconWrapper>
          <ModeColumn>
            <TextRow isTitle>
              <Title>{formatMessage({ id: 'eatIn' })}</Title>
            </TextRow>
            {dineInAvailable && (
              <PickupMode
                serviceMode={ServiceMode.EAT_IN}
                modeAvailable={!dineInDisabled}
                availableText={formatMessage({ id: 'dineIn' })}
                unavailableText={formatMessage({ id: 'dineInUnavailable' })}
                onSelected={props.selectServiceMode}
                testID="pickupmode-dine-in"
              />
            )}
          </ModeColumn>
        </Section>
      )}
    </ModalContent>
  );
};

export default PickupModeContent;
